import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';
import type { UserJob } from '../types';
import { SUBCOMPONENTS } from '../types';

interface UseAutoFinalGenerationParams {
  jobs: UserJob[];
  loadJobs: (isBackgroundRefresh?: boolean) => Promise<void>;
}

interface UseAutoFinalGenerationResult {
  autoFinalJobIds: Set<string>;
  setAutoFinalJobIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleGenerateResume: (jobid: string) => void;
}

export default function useAutoFinalGeneration({
  jobs,
  loadJobs,
}: UseAutoFinalGenerationParams): UseAutoFinalGenerationResult {
  const [autoFinalJobIds, setAutoFinalJobIds] = useState<Set<string>>(new Set());
  const autoFinalTriggered = useRef<Set<string>>(new Set());

  // Auto-final-generation: when all non-omit sections complete for a tracked job, trigger final files
  useEffect(() => {
    if (autoFinalJobIds.size === 0) return;

    autoFinalJobIds.forEach((jobid) => {
      // Don't trigger twice
      if (autoFinalTriggered.current.has(jobid)) return;

      const job = jobs.find((j) => j.jobid === jobid);
      if (!job) return;

      // Check if all non-omit sections are complete
      const allComplete = SUBCOMPONENTS.every((comp) => {
        const type = job[`type${comp}` as keyof UserJob] as string;
        if (type === 'omit') return true;
        const state = job[`state${comp}` as keyof UserJob] as string;
        return state === 'complete';
      });

      if (allComplete) {
        autoFinalTriggered.current.add(jobid);
        console.log(`Auto-generating final files for job ${jobid}`);
        api.generateFinalFiles(jobid).then(() => {
          loadJobs(true);
          // Clean up tracking after a delay
          setTimeout(() => {
            setAutoFinalJobIds((prev) => {
              const next = new Set(prev);
              next.delete(jobid);
              return next;
            });
            autoFinalTriggered.current.delete(jobid);
          }, 5000);
        }).catch((err) => {
          console.error(`Failed to auto-generate final files for ${jobid}:`, err);
          autoFinalTriggered.current.delete(jobid);
        });
      }
    });
  }, [jobs, autoFinalJobIds, loadJobs]);

  // Handle "Generate Resume" button from JobCard - start tracking for auto-final
  const handleGenerateResume = useCallback((jobid: string) => {
    setAutoFinalJobIds((prev) => new Set(prev).add(jobid));
  }, []);

  return { autoFinalJobIds, setAutoFinalJobIds, handleGenerateResume };
}
